export type Side = 0 | 1;

export const TRACK = 28;
export const LANE = 4;
export const LAST_TRACK = TRACK - 2;
export const HOME = LAST_TRACK + LANE + 1;
export const BASE = -1;
export const START: Record<Side, number> = { 0: 1, 1: 15 };

export type Rules = { pieces: number; needSix: boolean };

export type State = { pos: [number[], number[]] };

export const newGame = (r: Rules): State => ({ pos: [new Array(r.pieces).fill(BASE), new Array(r.pieces).fill(BASE)] });

export const trackCell = (side: Side, p: number) => (START[side] + p) % TRACK;

export const isSafe = (cell: number) => cell === START[0] || cell === START[1];

export function target(r: Rules, s: State, side: Side, piece: number, roll: number): number | null {
  const p = s.pos[side][piece];
  if (p === HOME) return null;
  if (p === BASE) return r.needSix && roll !== 6 ? null : 0;
  return Math.min(p + roll, HOME);
}

export function movable(r: Rules, s: State, side: Side, roll: number): number[] {
  const out: number[] = [];
  const seen = new Set<number>();
  s.pos[side].forEach((p, i) => {
    if (target(r, s, side, i, roll) === null) return;
    if (p === BASE && seen.has(BASE)) return;
    seen.add(p);
    out.push(i);
  });
  return out;
}

export type Move = { next: State; from: number; to: number; captured: number[] };

export function move(r: Rules, s: State, side: Side, piece: number, roll: number): Move | null {
  const to = target(r, s, side, piece, roll);
  if (to === null) return null;
  const from = s.pos[side][piece];
  const pos: [number[], number[]] = [[...s.pos[0]], [...s.pos[1]]];
  pos[side][piece] = to;
  const captured: number[] = [];
  if (to <= LAST_TRACK) {
    const cell = trackCell(side, to);
    if (!isSafe(cell)) {
      const other = (1 - side) as Side;
      pos[other].forEach((q, i) => {
        if (q !== BASE && q <= LAST_TRACK && trackCell(other, q) === cell) { pos[other][i] = BASE; captured.push(i); }
      });
    }
  }
  return { next: { pos }, from, to, captured };
}

export const won = (s: State, side: Side) => s.pos[side].every((p) => p === HOME);

export function steps(from: number, to: number): number[] {
  if (from === BASE) return [0];
  const out: number[] = [];
  for (let p = from + 1; p <= to; p++) out.push(p);
  return out;
}

export function choose(r: Rules, s: State, side: Side, roll: number): number | null {
  const options = movable(r, s, side, roll);
  if (options.length === 0) return null;
  const score = (i: number) => {
    const m = move(r, s, side, i, roll)!;
    let v = m.to;
    if (m.to === HOME) v += 100;
    if (m.captured.length) v += 80;
    if (m.from === BASE) v += 60;
    if (m.to <= LAST_TRACK && isSafe(trackCell(side, m.to))) v += 20;
    return v;
  };
  return options.reduce((a, b) => (score(b) > score(a) ? b : a));
}
